import CloseIcon from "@mui/icons-material/Close";
import { Button, IconButton, Snackbar, Stack, Typography } from "@mui/material";
import React, { useCallback, useEffect, useMemo, useRef } from "react";

import HNSColors from "../../../ui library/HNSColors";

import { useToastStore } from "./store/useToastStore";

const HEADER_OFFSET = "calc(var(--pn-header-height, 64px) + 12px)";

const ToastProvider = () => {
  const {
    backgroundColor,
    closeToast,
    duration,
    icon,
    isOpen,
    message,
    navigation,
    resetToast,
    textColor,
  } = useToastStore((state) => state);
  const resetTimeoutRef = useRef(null);

  const handleClose = useCallback(
    (_, reason) => {
      if (reason === "clickaway") {
        return;
      }
      closeToast();
      // Prevent to clear the info while closing (for animation purposes)
      if (resetTimeoutRef.current) {
        clearTimeout(resetTimeoutRef.current);
      }
      resetTimeoutRef.current = setTimeout(() => {
        resetToast();
        resetTimeoutRef.current = null;
      }, 500);
    },
    [closeToast, resetToast]
  );

  const navigationSlot = useMemo(() => {
    if (!navigation?.label) {
      return null;
    }

    const { label, onClick, txtColor } = navigation;

    return (
      <Button
        size="small"
        onClick={(event) => {
          if (onClick) {
            onClick(event);
          }
          handleClose(event, "navigation-click");
        }}
        sx={{
          ml: 2,
          px: 1,
          minWidth: "auto",
          fontWeight: 600,
          textTransform: "none",
          color: txtColor,
          "&:hover": {
            backgroundColor: "transparent",
            opacity: 0.8,
          },
        }}
      >
        {label}
      </Button>
    );
  }, [navigation, handleClose]);

  // Use `message` as string combined with `textColor` or `message` as React element
  const messageSlot = useMemo(() => {
    if (!message) {
      return null;
    }

    if (React.isValidElement(message)) {
      return message;
    }

    return (
      <Typography variant="body2" component="span" sx={{ color: textColor }}>
        {message}
      </Typography>
    );
  }, [message, textColor]);

  useEffect(() => {
    return () => {
      if (resetTimeoutRef.current) {
        clearTimeout(resetTimeoutRef.current);
      }
    };
  }, []);

  return (
    <Snackbar
      open={isOpen}
      anchorOrigin={{ vertical: "top", horizontal: "center" }}
      autoHideDuration={duration}
      onClose={handleClose}
      message={
        <Stack direction="row" spacing={1.5} alignItems="center">
          {icon ?? null}
          {messageSlot}
          {navigationSlot}
          <IconButton
            aria-label="Close toast"
            size="small"
            onClick={(event) => handleClose(event, "action-click")}
            sx={{
              color: HNSColors.neutrals[600],
              ml: 1,
            }}
          >
            <CloseIcon fontSize="medium" />
          </IconButton>
        </Stack>
      }
      sx={{
        top: HEADER_OFFSET,
        "& .MuiSnackbarContent-root": {
          width: "auto !important",
          minWidth: "auto !important",
          maxWidth: "90vw !important",
          backgroundColor,
          color: textColor,
          borderRadius: "8px",
          padding: "10px 16px",
          boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
        },
      }}
    />
  );
};

export default ToastProvider;
